import { HttpEvent, HttpHandler, HttpHeaders, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { Inject, Injectable, LOCALE_ID } from '@angular/core';
import { Observable, from, switchMap } from 'rxjs';
import { TenantService } from '../tenant/tenant.service';
import { SKIP_INTERCEPTOR, SKIP_ORGANISATION } from 'src/types/custom-headers';

const LOCAL_STORAGE_ORGANISATION_ID_NAME = 'organisationId';

@Injectable()
export class ApiInterceptorService implements HttpInterceptor {

  constructor(
    private readonly tenantService: TenantService,
    @Inject(LOCALE_ID) private readonly locale: string
  ) { }

  public intercept(req: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    if (!req.headers.has('Content-Language')) {
      req = req.clone({ headers: req.headers.set('Content-Language', this.locale) });
    }

    if (req.headers.has(SKIP_INTERCEPTOR)) {
      const apiRequest = req.clone({ headers: req.headers.delete(SKIP_INTERCEPTOR) });
      return next.handle(apiRequest);
    }

    // Relative assets and third-party requests must never receive the Cognito bearer token.
    if (!/^https?:\/\//i.test(req.url)) {
      return next.handle(req);
    }

    return from(this.tenantService.getApiUrl()).pipe(
      switchMap((apiUrl: string): Observable<HttpEvent<unknown>> => {
        if (!req.url.startsWith(apiUrl)) { return next.handle(req); }

        let headers = req.headers.delete('License');
        if (headers.has(SKIP_ORGANISATION)) {
          headers = headers.delete(SKIP_ORGANISATION);
        } else {
          const organisationId = localStorage.getItem(LOCAL_STORAGE_ORGANISATION_ID_NAME);
          if (organisationId) { headers = headers.set('organisationId', organisationId); }
        }

        return from(this.tenantService.getAuthorizationHeaders()).pipe(
          switchMap((authHeaders: HttpHeaders): Observable<HttpEvent<unknown>> => {
            authHeaders.keys().forEach((key: string): void => {
              headers = headers.set(key, authHeaders.get(key) ?? '');
            });
            return next.handle(req.clone({ headers, withCredentials: false }));
          })
        );
      })
    );
  }
}
